/**
 * Central "commercially paused" screen for a self-service team whose owner's
 * Trainnova Coach subscription no longer has commercial access.
 *
 * Owner/payer → activation/manage action (Stripe Customer Portal).
 * Member (player/parent/staff) → informational only, NEVER a purchase CTA.
 */
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { createAccountBillingPortal } from '../lib/api';
import { teamBillingAction, stripeDestination } from '../lib/teamBillingAction';
import { useAuth } from '../context/AuthContext';
import { PauseCircle, CreditCard, Loader2, Users } from 'lucide-react';

export function TeamPausedScreen(props) {
  const { user, sessionVersion } = useAuth();
  return <PausedContent key={JSON.stringify([props.team?.id, user?.id, sessionVersion])} {...props} />;
}

function PausedContent({ team, isOwner }) {
  const { t, i18n } = useTranslation(['self_onboarding']);
  const sv = (i18n.resolvedLanguage || i18n.language || 'sv').startsWith('sv');
  const separateTeam = team?.billing_model === 'organization_team_v1';
  const { teams, changeActiveTeam } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const lock = useRef(false);
  const alive = useRef(true);
  useEffect(()=>{alive.current=true;return()=>{alive.current=false;};},[]);

  const manage = async () => {
    if (!isOwner || lock.current) return;
    lock.current=true;setBusy(true);setError('');
    try {
      if(separateTeam) {
        const action=await teamBillingAction(team.id,()=>alive.current);
        if(!action)return;
        if(action.kind==='active')window.location.reload();
        else window.location.assign(action.url);
      } else {
        const res=await createAccountBillingPortal(window.location.origin+'/oversikt');
        if(alive.current)window.location.assign(stripeDestination(res.data.portal_url,'portal'));
      }
    } catch(e) {
      if(alive.current)setError(typeof e.response?.data?.detail==='string' ? e.response.data.detail : (sv ? 'Betalningen kunde inte öppnas. Försök igen.' : 'Could not open billing. Please try again.'));
    } finally { lock.current=false;if(alive.current)setBusy(false); }
  };

  // Other teams the user can switch to that DO have commercial access. Each
  // team's coverage is independent — switching never unlocks THIS team.
  const otherActiveTeams = (teams || []).filter(
    (tm) => tm.id !== team?.id && tm.commercial_access !== false
  );

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4">
      <Card className="bg-card border-border max-w-md w-full text-center" data-testid="team-paused-screen">
        <CardHeader>
          <PauseCircle className="h-12 w-12 text-[rgb(var(--tn-text-f59e0b))] mx-auto mb-2" />
          <CardTitle className="text-2xl">
            {separateTeam ? (sv ? 'Lagets abonnemang behöver aktiveras eller uppdateras' : 'The team subscription needs activation or an update') : isOwner ? t('self_onboarding:paused.owner_title') : t('self_onboarding:paused.member_title')}
          </CardTitle>
          <CardDescription>
            {separateTeam ? (isOwner ? (sv ? 'Öppna betalningen för detta lag. Dina andra abonnemang påverkas inte.' : 'Open billing for this team. Your other subscriptions are unaffected.') : (sv ? 'Kontakta lagets abonnemangsansvariga för hjälp.' : 'Contact the person responsible for the team subscription.')) : isOwner ? t('self_onboarding:paused.owner_body') : t('self_onboarding:paused.member_body')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          {isOwner && (
            <Button onClick={manage} disabled={busy} className="w-full h-12" data-testid="paused-manage-btn">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
              {separateTeam ? (sv ? 'Öppna lagets betalning' : 'Open team billing') : t('self_onboarding:paused.owner_cta')}
            </Button>
          )}
          {otherActiveTeams.length > 0 && (
            <div className="pt-1" data-testid="paused-switch-team">
              <p className="text-sm text-muted-foreground mb-2">{t('self_onboarding:paused.switch_label')}</p>
              <div className="space-y-2">
                {otherActiveTeams.map((tm) => (
                  <Button
                    key={tm.id}
                    variant="outline"
                    className="w-full h-11 justify-start"
                    disabled={busy}
                    onClick={() => changeActiveTeam(tm.id).catch(()=>setError(sv ? 'Kunde inte byta lag.' : 'Could not switch team.'))}
                    data-testid={`paused-switch-team-${tm.id}`}
                  >
                    <Users className="mr-2 h-4 w-4" />
                    {tm.name}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default TeamPausedScreen;
